import React, { useState } from 'react';
import { Search as SearchIcon, ArrowRight, Package, Building } from 'lucide-react';
import { Product, BrandProfile } from '../../types';
import { ProductCard } from '../shopping/ProductCard';

interface SearchViewProps {
  products: Product[];
  brands: BrandProfile[];
  wishlist: any[]; 
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelectProduct: (product: Product) => void;
  onToggleWishlist: (productId: string) => void; 
  onViewBrand: (brandName: string) => void;
}

export const SearchView: React.FC<SearchViewProps> = ({
  products,
  brands,
  wishlist,
  searchQuery,
  onSearchChange,
  onSelectProduct,
  onToggleWishlist,
  onViewBrand
}) => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'products' | 'brands'>('all');

  // Helper to safely check wishlist
  const isWishlisted = (productId: string) => {
    if (!wishlist) return false;
    return wishlist.some(item => 
      (typeof item === 'string' && item === productId) || 
      (typeof item === 'object' && item.productId === productId)
    );
  };

  // --- FILTERING LOGIC ---
  const filteredProducts = products.filter(product => {
    const q = (searchQuery || '').toLowerCase();
    const name = (product.name || '').toLowerCase();
    const brand = (product.brand || '').toLowerCase();
    const desc = (product.description || '').toLowerCase();
    
    return name.includes(q) || brand.includes(q) || desc.includes(q);
  });

  const filteredBrands = brands.filter(brand => {
    const b = brand as any; // Safe cast to access varying database fields
    const q = (searchQuery || '').toLowerCase();
    // 🟢 FIX: Check for businessName first, fallback to name
    const name = (b.businessName || b.name || '').toLowerCase();
    const desc = (b.description || '').toLowerCase();
    const bio = (b.bio || '').toLowerCase();

    return name.includes(q) || desc.includes(q) || bio.includes(q);
  });

  return (
    <div className="flex-1 flex flex-col h-full bg-black">
      
      {/* 🟢 FULL SCREEN DESKTOP CONTAINER */}
      <div className="w-full max-w-[1600px] mx-auto flex flex-col h-full">
          
          {/* SEARCH HEADER */}
          <div className="p-6 lg:p-12 lg:pb-6 border-b border-zinc-900">
            <div className="relative group">
              <SearchIcon className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-white transition-colors lg:w-8 lg:h-8" size={20} />
              <input
                type="text"
                placeholder="SEARCH BRANDS, SNEAKERS, FITS..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-[2rem] lg:rounded-none py-5 lg:py-8 pl-16 lg:pl-20 pr-6 text-white outline-none focus:border-white transition-all text-sm lg:text-2xl font-black uppercase tracking-tighter placeholder:text-zinc-700 shadow-2xl"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-3 mt-8 overflow-x-auto scrollbar-hide pb-2">
              {(['all', 'products', 'brands'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-8 py-3 lg:py-4 rounded-full lg:rounded-none text-xs lg:text-sm font-black uppercase tracking-[0.2em] transition-all shrink-0 ${
                    activeFilter === filter 
                      ? 'bg-white text-black' 
                      : 'bg-black text-zinc-500 border border-zinc-800 hover:border-white hover:text-white'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* RESULTS AREA */}
          <div className="flex-1 overflow-y-auto p-6 lg:p-12 pt-6 pb-32 scrollbar-hide">
            {searchQuery.length > 0 ? (
              <div className="space-y-16">
                
                {/* 🏢 BRANDS SECTION */}
                {(activeFilter === 'all' || activeFilter === 'brands') && filteredBrands.length > 0 && (
                  <div>
                    <h3 className="text-zinc-500 text-xs lg:text-sm font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-3">
                      <Building size={16} /> Brands
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredBrands.map(brand => {
                        const b = brand as any;
                        const brandName = b.businessName || b.name || 'Unknown Brand';
                        const brandLogo = b.logoUrl || b.logo || '';

                        return (
                          <button 
                            key={b.id}
                            // 🟢 FIX: Safely pass the resolved brandName
                            onClick={() => onViewBrand(brandName)}
                            className="flex items-center gap-5 bg-zinc-950 p-4 lg:p-6 rounded-2xl lg:rounded-none border border-zinc-800 hover:border-white hover:bg-zinc-900 transition-all group"
                          >
                            <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full lg:rounded-none border border-zinc-800 bg-black overflow-hidden shrink-0 flex items-center justify-center">
                                {/* 🟢 FIX: Safely render logoUrl */}
                                {brandLogo ? (
                                    <img src={brandLogo} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={brandName} />
                                ) : (
                                    <span className="text-[10px] font-black text-zinc-600">LOGO</span>
                                )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <h4 className="text-white font-black uppercase tracking-widest text-lg truncate mb-1">{brandName}</h4>
                              <p className="text-zinc-500 font-mono text-[10px] uppercase truncate">{b.bio || b.description || 'Verified Brand'}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full lg:rounded-none border border-zinc-800 flex items-center justify-center text-zinc-500 group-hover:text-black group-hover:bg-white group-hover:border-white transition-all shrink-0">
                                <ArrowRight size={16} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 👟 PRODUCTS SECTION */}
                {(activeFilter === 'all' || activeFilter === 'products') && filteredProducts.length > 0 && (
                  <div>
                    <h3 className="text-zinc-500 text-xs lg:text-sm font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-3">
                      <Package size={16} /> Products
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-8">
                      {filteredProducts.map(product => (
                        <ProductCard 
                          key={product.id}
                          product={product}
                          onClick={onSelectProduct} 
                          isWishlisted={isWishlisted(product.id)} 
                          onToggleWishlist={() => onToggleWishlist(product.id)} 
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* EMPTY STATE (Search with no matches) */}
                {filteredBrands.length === 0 && filteredProducts.length === 0 && (
                  <div className="text-center py-32 flex flex-col items-center justify-center opacity-30">
                    <SearchIcon size={64} strokeWidth={1} className="text-white mb-6" />
                    <p className="text-white font-black uppercase tracking-[0.3em] text-lg">No matches found</p>
                    <p className="text-zinc-400 font-mono text-xs mt-2 uppercase tracking-widest">Try a different search term</p>
                  </div>
                )}
              </div>
            ) : (
              
              /* INITIAL EMPTY STATE (Before searching) */
              <div className="h-full flex flex-col items-center justify-center py-20 lg:py-32 opacity-20">
                <SearchIcon size={100} strokeWidth={1} className="mb-8 text-white" />
                <h2 className="text-4xl lg:text-6xl font-black text-white italic tracking-tighter uppercase mb-4">Explore</h2>
                <p className="text-zinc-300 font-mono text-xs lg:text-sm uppercase tracking-[0.3em]">Search the archive</p>
              </div>
            )}
          </div>
          
      </div>
    </div>
  );
};